/**
 * The period every report shares.
 *
 * Choosing "Last quarter" on the control tower and then opening Recycling
 * used to drop you back on this month; now the period is one value for the
 * whole session, and it lives in the address bar so a report can be
 * bookmarked or sent to someone and opens on the same dates.
 *
 * A module-level store rather than React context: the shell, the picker and
 * every report read it, and none of them should re-render the others to
 * change it.
 */

import { useSyncExternalStore } from "react";
import {
  defaultPeriod,
  periodFromParams,
  periodToParams,
  type Compare,
  type Period,
} from "./period";

interface State {
  period: Period;
  compare: Compare;
}

function initial(): State {
  try {
    const fromUrl = periodFromParams(new URLSearchParams(window.location.search));
    if (fromUrl) return fromUrl;
  } catch {
    // No window (tests) or an unreadable URL: fall through to the default.
  }
  return { period: defaultPeriod(), compare: "prior" };
}

let state: State = initial();
const listeners = new Set<() => void>();

function writeUrl(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    periodToParams(state.period, state.compare, params);
    const url = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
    // Replace, not push: stepping through twelve months should not leave
    // twelve entries for the Back button to walk through.
    window.history.replaceState(window.history.state, "", url);
  } catch {
    // The address bar is a convenience; the report works without it.
  }
}

function set(next: Partial<State>): void {
  state = { ...state, ...next };
  writeUrl();
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The shared period, and a setter. */
export function useReportPeriod(): [Period, (period: Period) => void] {
  const period = useSyncExternalStore(subscribe, () => state.period);
  return [period, setReportPeriod];
}

/** What the shared period's changes are compared against. */
export function useCompare(): [Compare, (compare: Compare) => void] {
  const compare = useSyncExternalStore(subscribe, () => state.compare);
  return [compare, setCompare];
}

export function setReportPeriod(period: Period): void {
  set({ period });
}

export function setCompare(compare: Compare): void {
  set({ compare });
}

/** Put the current period in the address bar, e.g. when a report opens. */
export function syncPeriodToUrl(): void {
  writeUrl();
}
